/**
 * Unified WebSocket Service
 * 
 * Single source of truth for all WebSocket connections with:
 * - JWT authentication
 * - Channel-based routing
 * - Auto-reconnection with exponential backoff
 * - Type-safe message handling
 * - Comprehensive error handling
 */

import {
  WebSocketChannel,
  WebSocketMessage,
  WebSocketMessageType,
  WebSocketConnectionState,
  WebSocketConnectionInfo,
  WebSocketEventHandlers,
  WebSocketConfig,
  WebSocketError,
  WebSocketMetrics,
  ChannelSubscription,
  WebSocketAuthRequest,
  ChannelSubscribeRequest,
  ChannelUnsubscribeRequest,
  BaseWebSocketMessage
} from '../types/websocket';

export class UnifiedWebSocketService {
  private ws: WebSocket | null = null;
  private config: Required<WebSocketConfig>;
  private connectionInfo: WebSocketConnectionInfo;
  private eventHandlers: WebSocketEventHandlers = {};
  private messageQueue: WebSocketMessage[] = [];
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private authTimeout: NodeJS.Timeout | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private metrics: WebSocketMetrics;
  
  // Message acknowledgment tracking
  private pendingMessages = new Map<string, { 
    resolve: (value: any) => void; 
    reject: (error: Error) => void; 
    timeout: NodeJS.Timeout;
  }>();

  constructor(config: WebSocketConfig = {}) {
    this.config = {
      url: config.url || this.getDefaultWebSocketUrl(),
      token: config.token || '',
      autoConnect: config.autoConnect ?? true,
      autoReconnect: config.autoReconnect ?? true,
      maxReconnectAttempts: config.maxReconnectAttempts ?? 10,
      reconnectDelay: config.reconnectDelay ?? 1000,
      heartbeatInterval: config.heartbeatInterval ?? 30000,
      authTimeout: config.authTimeout ?? 10000,
      messageTimeout: config.messageTimeout ?? 5000,
      protocols: config.protocols ?? [],
      headers: config.headers ?? {}
    };

    this.connectionInfo = {
      state: WebSocketConnectionState.DISCONNECTED,
      url: this.config.url,
      protocol: '',
      reconnectAttempts: 0,
      maxReconnectAttempts: this.config.maxReconnectAttempts,
      reconnectDelay: this.config.reconnectDelay,
      subscribedChannels: new Set()
    };

    this.metrics = {
      messagesReceived: 0,
      messagesSent: 0,
      reconnections: 0,
      uptime: 0,
      lastActivity: new Date(),
      averageLatency: 0
    };

    if (this.config.autoConnect) {
      this.connect();
    }
  }

  // ==================== Public API ====================

  /**
   * Connect to WebSocket server
   */
  public async connect(): Promise<void> {
    if (this.connectionInfo.state === WebSocketConnectionState.CONNECTING ||
        this.connectionInfo.state === WebSocketConnectionState.CONNECTED) {
      return;
    }

    return new Promise((resolve, reject) => {
      try {
        this.setConnectionState(WebSocketConnectionState.CONNECTING);
        
        console.log(`[WebSocket] Connecting to ${this.config.url}`);
        
        this.ws = new WebSocket(
          this.config.url,
          this.config.protocols.length > 0 ? this.config.protocols : undefined
        );

        this.ws.onopen = () => {
          console.log('[WebSocket] Connected successfully');
          this.setConnectionState(WebSocketConnectionState.CONNECTED);
          this.connectionInfo.connectedAt = new Date();
          this.connectionInfo.reconnectAttempts = 0;
          
          this.startHeartbeat();
          this.processMessageQueue();
          
          if (this.config.token) {
            this.authenticate();
          }
          
          this.eventHandlers.onConnect?.();
          resolve();
        };

        this.ws.onmessage = (event) => this.handleMessage(event);
        this.ws.onerror = (event) => this.handleError(event, reject);
        this.ws.onclose = (event) => this.handleClose(event);

      } catch (error) {
        console.error('[WebSocket] Connection error:', error);
        this.setConnectionState(WebSocketConnectionState.ERROR);
        reject(error);
      }
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  public disconnect(): void {
    this.config.autoReconnect = false;
    this.clearTimeouts();
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
    }
    
    this.setConnectionState(WebSocketConnectionState.DISCONNECTED);
  }

  /**
   * Send a message with optional acknowledgment
   */
  public async sendMessage<T = any>(message: any, requireAck = false): Promise<T | void> {
    const fullMessage = {
      ...message,
      timestamp: new Date().toISOString(),
      id: requireAck ? this.generateMessageId() : undefined,
    };

    if (this.connectionInfo.state !== WebSocketConnectionState.AUTHENTICATED &&
        this.connectionInfo.state !== WebSocketConnectionState.CONNECTED) {
      this.messageQueue.push(fullMessage);
      
      if (this.config.autoReconnect && this.connectionInfo.state === WebSocketConnectionState.DISCONNECTED) {
        this.reconnect();
      }
      
      if (!requireAck) return;
    }

    if (requireAck && fullMessage.id) {
      return new Promise<T>((resolve, reject) => {
        const timeout = setTimeout(() => {
          this.pendingMessages.delete(fullMessage.id!);
          reject(new Error('Message timeout'));
        }, this.config.messageTimeout);

        this.pendingMessages.set(fullMessage.id!, { resolve, reject, timeout });
        this.sendRawMessage(fullMessage);
      });
    } else {
      this.sendRawMessage(fullMessage);
    }
  }

  /**
   * Subscribe to a channel
   */
  public async subscribeToChannel(
    channel: WebSocketChannel,
    filters?: Record<string, any>
  ): Promise<void> {
    const message: ChannelSubscribeRequest = {
      type: 'subscribe',
      channel,
      filters
    };

    await this.sendMessage(message, true);
    this.connectionInfo.subscribedChannels.add(channel);
    console.log(`[WebSocket] Subscribed to channel: ${channel}`);
  }

  /**
   * Unsubscribe from a channel
   */
  public async unsubscribeFromChannel(channel: WebSocketChannel): Promise<void> {
    const message: ChannelUnsubscribeRequest = {
      type: 'unsubscribe',
      channel
    };

    await this.sendMessage(message, true);
    this.connectionInfo.subscribedChannels.delete(channel);
    console.log(`[WebSocket] Unsubscribed from channel: ${channel}`);
  }

  /**
   * Set event handlers
   */
  public on(
    event: string,
    handler: any
  ): void {
    (this.eventHandlers as any)[event] = handler;
  }

  /**
   * Remove event handler
   */
  public off(event: string): void {
    delete (this.eventHandlers as any)[event];
  }

  /**
   * Get connection info
   */
  public getConnectionInfo(): WebSocketConnectionInfo {
    return { ...this.connectionInfo };
  }

  /**
   * Get metrics
   */
  public getMetrics(): WebSocketMetrics {
    const now = new Date();
    const uptime = this.connectionInfo.connectedAt 
      ? now.getTime() - this.connectionInfo.connectedAt.getTime()
      : 0;

    return {
      ...this.metrics,
      uptime: Math.floor(uptime / 1000)
    };
  }

  /**
   * Update authentication token
   */
  public updateToken(token: string): void {
    this.config.token = token;
    
    if (this.connectionInfo.state === WebSocketConnectionState.CONNECTED) {
      this.authenticate();
    }
  }

  // ==================== Private Methods ====================

  private getDefaultWebSocketUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL.replace(/^https?:/, protocol) + '/communication/ws';
    }

    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return `${protocol}//localhost:8000/api/v1/communication/ws`;
    }

    return `${protocol}//${window.location.hostname}/api/v1/communication/ws`;
  }

  private async authenticate(): Promise<void> {
    if (!this.config.token) {
      console.warn('[WebSocket] No token provided for authentication');
      return;
    }

    this.setConnectionState(WebSocketConnectionState.AUTHENTICATING);

    const authMessage: WebSocketAuthRequest = {
      type: 'auth',
      token: this.config.token
    };

    // Set auth timeout
    this.authTimeout = setTimeout(() => {
      this.handleAuthFailure('Authentication timeout');
    }, this.config.authTimeout);

    // Send directly via sendRawMessage to bypass state check in sendMessage,
    // which would queue the message since state is AUTHENTICATING (not CONNECTED)
    this.sendRawMessage(authMessage as any);
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);
      this.metrics.messagesReceived++;
      this.metrics.lastActivity = new Date();

      console.log('[WebSocket] Received message:', message.type, message);

      // Handle system messages
      const messageType = message.type as string;
      switch (messageType) {
        case 'auth_response':
          this.handleAuthResponse(message as any);
          break;
          
        case 'subscription_response':
          this.handleSubscriptionResponse(message as any);
          break;
          
        case 'error':
          this.handleServerError(message as any);
          break;
          
        case 'pong':
          this.connectionInfo.lastHeartbeat = new Date();
          break;
          
        default:
          // Handle acknowledgment for pending messages
          if (message.id && this.pendingMessages.has(message.id)) {
            const pending = this.pendingMessages.get(message.id)!;
            clearTimeout(pending.timeout);
            this.pendingMessages.delete(message.id);
            pending.resolve(message);
          }
          
          // Call type-specific handler
          const typeHandler = (this.eventHandlers as any)[message.type];
          if (typeHandler) {
            typeHandler((message as any).payload, message);
          }
          
          // Call generic message handler
          this.eventHandlers.onMessage?.(message, message);
          break;
      }
    } catch (error) {
      console.error('[WebSocket] Message parse error:', error, event.data);
    }
  }

  private handleAuthResponse(message: any): void {
    if (this.authTimeout) {
      clearTimeout(this.authTimeout);
      this.authTimeout = null;
    }

    if (message.success) {
      console.log('[WebSocket] Authentication successful');
      this.setConnectionState(WebSocketConnectionState.AUTHENTICATED);
      this.eventHandlers.onAuthSuccess?.(message.user_id, message.channels || []);
    } else {
      console.error('[WebSocket] Authentication failed:', message.error);
      this.handleAuthFailure(message.error || 'Authentication failed');
    }
  }

  private handleSubscriptionResponse(message: any): void {
    if (message.success) {
      this.eventHandlers.onChannelSubscribed?.(message.channel);
    } else {
      console.error(`[WebSocket] Subscription to ${message.channel} failed:`, message.error);
      this.connectionInfo.subscribedChannels.delete(message.channel);
    }
  }

  private handleServerError(message: any): void {
    console.error('[WebSocket] Server error:', message.error);
    
    const error: WebSocketError = {
      code: message.code || 'UNKNOWN_ERROR',
      message: message.error || 'Unknown server error',
      details: message.details,
      timestamp: new Date()
    };
    
    this.eventHandlers.onError?.(new Error(error.message));
  }

  private handleAuthFailure(reason: string): void {
    console.error('[WebSocket] Authentication failed:', reason);
    this.setConnectionState(WebSocketConnectionState.ERROR);
    this.eventHandlers.onAuthFailure?.(reason);
    
    // Disconnect on auth failure
    this.disconnect();
  }

  private handleError(event: Event, rejectConnect?: (error: Error) => void): void {
    console.error('[WebSocket] Connection error:', event);
    this.setConnectionState(WebSocketConnectionState.ERROR);
    
    const error = new Error('WebSocket connection error');
    this.eventHandlers.onError?.(error);
    
    if (rejectConnect) {
      rejectConnect(error);
    }
  }

  private handleClose(event: CloseEvent): void {
    console.log(`[WebSocket] Connection closed: ${event.code} - ${event.reason}`);
    
    this.clearTimeouts();
    this.setConnectionState(WebSocketConnectionState.DISCONNECTED);
    this.connectionInfo.subscribedChannels.clear();
    
    this.eventHandlers.onDisconnect?.(event.reason || 'Connection closed');

    // Auto-reconnect if enabled and not a normal close
    if (this.config.autoReconnect && event.code !== 1000) {
      this.reconnect();
    }
  }

  private reconnect(): void {
    if (this.connectionInfo.reconnectAttempts >= this.connectionInfo.maxReconnectAttempts) {
      console.error('[WebSocket] Max reconnection attempts reached');
      this.setConnectionState(WebSocketConnectionState.ERROR);
      return;
    }

    const delay = Math.min(
      this.config.reconnectDelay * Math.pow(2, this.connectionInfo.reconnectAttempts),
      30000 // Max 30 seconds
    );

    this.connectionInfo.reconnectAttempts++;
    this.metrics.reconnections++;
    
    console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${this.connectionInfo.reconnectAttempts})`);
    
    this.setConnectionState(WebSocketConnectionState.RECONNECTING);
    
    this.reconnectTimeout = setTimeout(() => {
      this.connect().catch(error => {
        console.error('[WebSocket] Reconnection failed:', error);
        this.reconnect();
      });
    }, delay);
  }

  private sendRawMessage(message: WebSocketMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[WebSocket] Cannot send message - connection not open');
      return;
    }

    try {
      const data = JSON.stringify(message);
      this.ws.send(data);
      this.metrics.messagesSent++;
      this.metrics.lastActivity = new Date();
      
      console.log('[WebSocket] Sent message:', message.type, message);
    } catch (error) {
      console.error('[WebSocket] Send error:', error);
    }
  }

  private processMessageQueue(): void {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift()!;
      this.sendRawMessage(message);
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.connectionInfo.state === WebSocketConnectionState.CONNECTED ||
          this.connectionInfo.state === WebSocketConnectionState.AUTHENTICATED) {
        this.sendMessage({ type: WebSocketMessageType.PING });
      }
    }, this.config.heartbeatInterval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private clearTimeouts(): void {
    this.stopHeartbeat();
    
    if (this.authTimeout) {
      clearTimeout(this.authTimeout);
      this.authTimeout = null;
    }
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    // Clear pending message timeouts
    this.pendingMessages.forEach(pending => clearTimeout(pending.timeout));
    this.pendingMessages.clear();
  }

  private setConnectionState(state: WebSocketConnectionState): void {
    const oldState = this.connectionInfo.state;
    this.connectionInfo.state = state;
    
    if (oldState !== state) {
      console.log(`[WebSocket] State changed: ${oldState} -> ${state}`);
    }
  }

  private generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// ==================== Singleton Instance ====================

let websocketServiceInstance: UnifiedWebSocketService | null = null;

export const getWebSocketService = (config?: WebSocketConfig): UnifiedWebSocketService => {
  if (!websocketServiceInstance) {
    websocketServiceInstance = new UnifiedWebSocketService(config);
  }
  return websocketServiceInstance;
};

export const resetWebSocketService = (): void => {
  if (websocketServiceInstance) {
    websocketServiceInstance.disconnect();
    websocketServiceInstance = null;
  }
};

export default UnifiedWebSocketService;